/**
 * Transform functions to map v4 API response shapes to v3 model formats.
 * The v3 Redux reducers construct Immutable Records from these objects,
 * so the returned shapes must match the v3 model constructor fields.
 */

import { getV3TypeIdForV4Type, isV4OnlyType, getV4Brand, deviceTypesInv } from '../../utils/models/DeviceType';
import { v4PropertiesToExtraFields } from '../../utils/models/v4DeviceProperties';
import { v4CapabilitiesToDeviceFlags } from '../../utils/models/v4DeviceCapabilities';
import { v4AlarmOrigin } from '../../utils/models/v4AlarmOrigin';
import alarmTypes from "../../utils/models/AlarmTypes";
import { ASSISTANCE_REQUEST_META, metadataLinkId } from '../../utils/models/v4AlarmSituation';

/**
 * Detect mobile OS ('ios' | 'android') from V4 device metadata.
 * V4 mobile devices store an `app-push-token` value with the format
 * "ios:<token>" or "android:<token>" — we use that prefix as the OS hint.
 * Falls back to scanning other metadata values for an os prefix.
 */
function detectV4DeviceOs(metadata) {
    if (!metadata) return '';
    const fromPushToken = (metadata['app-push-token'] || '').toLowerCase();
    if (fromPushToken.startsWith('ios:')) return 'ios';
    if (fromPushToken.startsWith('android:')) return 'android';
    const explicitOs = (metadata.os || metadata.platform || '').toLowerCase();
    if (explicitOs === 'ios' || explicitOs === 'android') return explicitOs;
    return '';
}

/**
 * Returns true when the timestamp represents an unset / zero value from the V4 API.
 */
const isZeroTimestamp = (ts) => !ts || String(ts).startsWith('0001-');

/**
 * Evaluates the connection status of a V4 device based on its ingress_connection.
 *
 * @param {object} ingressConnection - The ingress_connection object from the V4 device.
 * @returns {{ online: boolean, onlineSince: Date|null, offlineSince: Date|null, offlineReason: string|null }}
 */
export const evaluateDeviceStatus = (ingressConnection) => {
    if (!ingressConnection || isZeroTimestamp(ingressConnection.created)) {
        return { online: false, onlineSince: null, offlineSince: null, offlineReason: 'never_connected' };
    }

    if (isZeroTimestamp(ingressConnection.closed_at)) {
        return {
            online: true,
            onlineSince: new Date(ingressConnection.created),
            offlineSince: null,
            offlineReason: null,
        };
    }

    return {
        online: false,
        onlineSince: null,
        offlineSince: new Date(ingressConnection.closed_at),
        offlineReason: ingressConnection.closed_reason || 'disconnected',
    };
};

/**
 * Maps a v4 Device to the v3 Device model shape.
 *
 * V4: { id (UUID), owner_id, name, type, capabilities, metadata, ingress_connection }
 * V3: { id, name, status, uuid, uniqueId, accountId, deviceTypeId, ... }
 */
export function mapV4DeviceToV3(v4Device) {
    const connectionStatus = evaluateDeviceStatus(v4Device.ingress_connection);

    // If online → "connected since"; if offline → "disconnected since". During a reboot
    // `online` flips before ingress_connection closes, so only use a timestamp when the
    // two agree — 0 hides the tooltip instead of showing the stale connect time.
    const online = !!v4Device.online;
    let lastUpdateTs = 0;
    if (online && connectionStatus.onlineSince) {
        lastUpdateTs = Math.floor(connectionStatus.onlineSince.getTime() / 1000);
    } else if (!online && connectionStatus.offlineSince) {
        lastUpdateTs = Math.floor(connectionStatus.offlineSince.getTime() / 1000);
    }

    const mappedTypeId = getV3TypeIdForV4Type(v4Device.type);
    const v4Only = isV4OnlyType(v4Device.type);
    const os = detectV4DeviceOs(v4Device.metadata);
    const batteryRaw = v4Device.metadata?.battery_level;
    const battery = batteryRaw != null && batteryRaw !== '' ? +batteryRaw : null;
    // Cuebly mobile devices live only in V4 — surface them as App devices in V3
    // so that existing UI (DeviceLabels, brand icons) treats them like apps.
    const v4Brand = getV4Brand(v4Device.type);
    const isCueblyMobile = v4Brand === 'cuebly-mobile' || !!os;
    // Name-based fallback: `getV4Brand` depends on the device-type defs cache
    // which may not be populated yet when devices arrive.
    const isTwigV4 = v4Brand === 'twig' || /^twig/i.test(v4Device.type || '');
    const deviceTypeId = mappedTypeId || (isCueblyMobile ? deviceTypesInv.App : 0);

    const systemProps = v4Device.properties?.system || {};
    const operatingAddress = systemProps['operating_address']?.value || '';
    const tempAddress = systemProps['custom_address']?.value || '';

    return {
        id: v4Device.id,
        uuid: v4Device.id,
        name: v4Device.name || '',
        status: online ? 'online' : 'offline',
        // e.g. `requested_power_off`; null while online.
        disconnectReason: online ? null : connectionStatus.offlineReason,
        // Distinct from online — gates messaging (OSM) in the device list.
        available: v4Device.available !== false,
        activeState: 2, // active
        configState: 2, // configured
        accountId: v4Device.metadata?.['v3-account-id'] ? +v4Device.metadata['v3-account-id'] : 0,
        deviceTypeId,
        os,
        v4Type: v4Device.type || null,
        v4Metadata: v4Device.metadata || null,
        v4OwnerId: v4Device.owner_id || null,
        // Raw map kept so the form can tell which capabilities apply (e.g. ripcord)
        // when the V3 deviceTypeId is unmapped.
        v4Capabilities: v4Device.capabilities || null,
        // V4 reports firmware in metadata; surface it on the v3 `firmware` field.
        firmware: v4Device.metadata?.firmwareVersion || '',
        extraFields: v4PropertiesToExtraFields(v4Device.properties),
        operatingAddress,
        tempAddress,
        uniqueId: v4Device.metadata?.imei || v4Device.metadata?.unique_id || '',
        lastUpdate: lastUpdateTs,
        battery,
        batteryDate: battery != null ? lastUpdateTs : null,
        // Capability flags (srd/ble/wifi/gnss/3g/4g/voLTE/manDown/ripcord) for the form checkboxes.
        ...v4CapabilitiesToDeviceFlags(v4Device.capabilities),
        // V3-backend capability flags the DeviceCard checks via `device.can(...)`.
        ...(v4Device.type ? {
            compatibility: {
                ...((v4Only || isTwigV4) && { osm: true }),
                devCon: true,
            },
        } : {}),
    };
}

/**
 * Maps a v4 ARC to the v3 ARC model shape so that V4 ARCs can live side-by-side
 * with V3 ARCs in the same Redux-stored list and render through the existing
 * admin UI without branching logic at every component.
 *
 * V4: { id, owner_id, name, label, label_color, trusted_numbers,
 *       emergency_numbers, endpoints, account_links, device_links,
 *       account_link_requirements, device_link_requirements }
 * V3: { id, key, name, color, label, email, proms, whitelistNumbers,
 *       emergencyNumbers, variables, salesItem, accounts, devices }
 *
 * The v4Data field preserves the full V4 response so downstream screens
 * (endpoint editor, link-requirement editor, device/account linking) can
 * access fields that don't map cleanly to V3.
 */
export function mapV4ArcToV3(v4Arc) {
    return {
        id: v4Arc.id,
        // V4 has no "key" concept — reuse the UUID so the existing forKey/arcKey
        // lookup paths keep working without a schema change.
        key: v4Arc.id,
        name: v4Arc.name || '',
        label: v4Arc.label || '',
        color: v4Arc.label_color || '',
        email: '',
        // V4 doesn't expose PROM numbers yet — leave empty so `arc.hasProms` is
        // false and PROM-based UI branches naturally skip V4 ARCs.
        proms: [],
        whitelistNumbers: v4Arc.trusted_numbers || [],
        emergencyNumbers: v4Arc.emergency_numbers || [],
        variables: [],
        salesItem: null,
        accounts: (v4Arc.account_links || []).length,
        devices: (v4Arc.device_links || []).length,
        // When true the ARC has exclusive rights to close its alarms.
        exclusiveClosing: !!v4Arc.exclusive_closing,
        source: 'v4',
        // Keep the raw V4 payload around for V4-specific editors / link flows.
        v4Data: v4Arc,
    };
}

// V4 LocationType — 0: indoor, 1: outdoor.
const V4_LOCATION_OUTDOOR = 1;
// V4 LocationDeterminationReason — 4: fixed.
const V4_LOCATION_REASON_FIXED = 4;

// Strongest signal first.
function v4ObservedBeacons(location) {
    return (location.observed_beacons || [])
        .filter((b) => b?.address)
        .map(({address, rssi}) => ({address, rssi}))
        .sort((a, b) => (b.rssi ?? -Infinity) - (a.rssi ?? -Infinity));
}

/**
 * Maps a v4 Location (from /v1/locations/{device_id}) to the v3 Position shape.
 * Returns null when the location lacks usable GNSS coordinates.
 */
export function mapV4LocationToV3Position(location) {
    if (!location?.gnss?.latitude || !location?.gnss?.longitude) return null;
    const toUnix = (ts) => (isZeroTimestamp(ts) ? 0 : Math.floor(new Date(ts).getTime() / 1000));
    const determinedAt = toUnix(location.determined_at);
    const observedBeacons = v4ObservedBeacons(location);
    // Indoor is the zero value, so only an explicit outdoor rules beacons out.
    const fromBeacons = observedBeacons.length > 0 && location.type !== V4_LOCATION_OUTDOOR;
    return {
        source: fromBeacons ? 'beacons' : 'gps',
        observedBeacons,
        fixed: location.reason === V4_LOCATION_REASON_FIXED,
        id: location.id,
        latitude: location.gnss.latitude,
        longitude: location.gnss.longitude,
        altitude: location.gnss.altitude || 0,
        address: location.geocoded?.formatted || location.geocoded?.address || '',
        // An alarm's embedded fix has a zero `created_at`.
        serverTime: toUnix(location.created_at) || determinedAt,
        deviceTime: determinedAt,
        fixTime: determinedAt,
        deviceId: location.device_id,
    };
}

// Newest fix per device — nothing promises the list holds just one each.
export function latestLocationByDevice(locations) {
    const byDevice = new Map();
    (Array.isArray(locations) ? locations : []).forEach((l) => {
        if (!l?.device_id) return;
        const prev = byDevice.get(l.device_id);
        if (!prev || new Date(l.created_at || 0) > new Date(prev.created_at || 0)) {
            byDevice.set(l.device_id, l);
        }
    });
    return byDevice;
}

/**
 * Maps a v4 HistoryEvent to the v3 Event model shape.
 *
 * V4: { id, eventType, timestamp, payload }
 * V3: { id, date (unix), event, title, message, deviceId, uiState, ... }
 */
export function mapV4EventToV3(v4Event) {
    return {
        id: v4Event.id,
        date: Math.floor(new Date(v4Event.timestamp).getTime() / 1000),
        event: v4Event.eventType || '',
        title: v4Event.eventType || '',
        message: '',
        payload: v4Event.payload || {},
        deviceId: v4Event.payload?.device_id || 0,
        uiState: true, // default to closed
        isTest: false,
    };
}

const v4StatusToV3State = {
    0: 0, // Open → OPEN
    1: 0, // Accepted → still OPEN (acceptance doesn't close it)
    2: 1, // Closed → CLOSED
    3: 5, // Expired → CLOSED_EXPIRED
};

const v4TypeToV3Type = {
    "sos": alarmTypes.SOS,
    "low_priority": alarmTypes.LOW_PRIO_ALARM,
    "assault": alarmTypes.SOS,
    "tilt": alarmTypes.MAN_DOWN,
    "immobility": alarmTypes.MAN_DOWN,
    "man_down": alarmTypes.MAN_DOWN,
    "assistance_request": alarmTypes.GENERIC_ALARM,
    "ripcord": alarmTypes.RIPCORD,
    "timer": alarmTypes.AMBER,
    "generic": alarmTypes.GENERIC_ALARM,
    "system_activated": alarmTypes.GENERIC_ALARM,
    "low_battery": alarmTypes.GENERIC_ALARM,
    "beacon_low_battery": alarmTypes.GENERIC_ALARM,
    "white_zone": alarmTypes.GENERIC_ALARM,
};

// in v4 we have
// const (
// SOS               AlarmType = "sos"
// LowPriority       AlarmType = "low_priority"
// Assault           AlarmType = "assault"
// Tilt              AlarmType = "tilt"
// Immobility        AlarmType = "immobility"
// ManDown           AlarmType = "man_down"
// AssistanceRequest AlarmType = "assistance_request"
// Ripcord           AlarmType = "ripcord"
// Timer             AlarmType = "timer"
// Generic           AlarmType = "generic"
// SystemActivated   AlarmType = "system_activated"
// LowBattery        AlarmType = "low_battery"
// BeaconLowBattery  AlarmType = "beacon_low_battery"
// WhiteZone         AlarmType = "white_zone"
// )

// V3 passthroughs carry placeholder ids (-3); only a positive id is a real V3 record.
const v3RecordId = (value) => (+value > 0 ? +value : 0);

/**
 * Maps a v4 Alarm to the v3 Alarm model shape so V4 alarms render through the
 * existing AlarmList / AlarmDetails UI without per-component branching.
 *
 * V4: { id (UUID), owner_id, created_at, updated_at, type, origin, location,
 *       receivers, status, metadata }
 * V3: { id, date (unix), state, type, deviceId, deviceName, accountId,
 *       accountName, notes, ... } — see utils/models/Alarm.js
 *
 * An alarm names an `origin`, not a device — a user, an API token or the platform may
 * have raised it. The V3 shape has only a device to hang that on, so `deviceId`/`deviceName`
 * carry whichever entity it was and `origin` says which kind.
 *
 * The `v4: true` flag tells the alarm saga to route close calls through the
 * V4 endpoint; `v4Data` preserves the raw payload for V4-specific flows.
 */
export function mapV4AlarmToV3(v4Alarm) {
    const toUnix = (ts) => (ts ? Math.floor(new Date(ts).getTime() / 1000) : 0);
    const origin = v4AlarmOrigin(v4Alarm);
    // Only a device carries the V3 account back-reference.
    const accountId = origin.device?.metadata?.['v3-account-id']
        ? +origin.device.metadata['v3-account-id']
        : 0;
    return {
        id: v4Alarm.id,
        date: toUnix(v4Alarm.created_at),
        checked: toUnix(v4Alarm.updated_at),
        state: v4StatusToV3State[v4Alarm.status] ?? 0,
        type: v4TypeToV3Type[v4Alarm.type?.toLowerCase()] ?? 'GENERIC_ALARM',
        deviceId: origin.id,
        deviceName: origin.name,
        origin,
        // Kept apart from `type`, which collapses e.g. assistance requests into GENERIC_ALARM.
        v4Type: (v4Alarm.type || '').toLowerCase(),
        sourceAlarmId: metadataLinkId(v4Alarm.metadata?.[ASSISTANCE_REQUEST_META.SOURCE_ALARM_ID]),
        followUpOfId: metadataLinkId(v4Alarm.metadata?.[ASSISTANCE_REQUEST_META.FOLLOW_UP_OF]),
        // Set when a V3 device forwarded the alarm to a V4 ARC.
        v3AlarmId: v3RecordId(v4Alarm.metadata?.v3_alarm_id),
        v3DeviceId: v3RecordId(v4Alarm.metadata?.v3_device_id),
        // Keep the API-provided actor id in websocket_url intact.
        rtcRoom: v4Alarm.rtc_room || null,
        accountId,
        // V4 has no cancelable / arc / token / share concepts yet — leave the
        // V3 defaults so those UI branches naturally hide for V4 alarms.
        cancelable: false,
        arc: null,
        // ARC-forwarded alarms mirror the ARC's `exclusive_closing`.
        exclusiveClosing: !!v4Alarm.arc_forward?.exclusive_closing,
        // Drives the dark icon + status ring in the alarm list.
        arcForward: v4Alarm.arc_forward || null,
        token: '',
        notes: v4Alarm.metadata?.notes || '',
        payload: v4Alarm.metadata || {},
        v4: true,
        v4Data: v4Alarm,
    };
}
